/**
 * The GA4 property this app reports to.
 *
 * There is exactly one measurement id, and the only question worth asking here
 * is whether it is real: the id arrives from build-time config, where it may
 * still be an unsubstituted placeholder.
 */
import { MEASUREMENT_ID_PATTERN } from './analytics.constants';
import type { MeasurementId } from './analytics.types';

/** Runtime half of the `MeasurementId` check, since the id comes from config. */
export function isMeasurementId(value: unknown): value is MeasurementId {
  return typeof value === 'string' && MEASUREMENT_ID_PATTERN.test(value.trim());
}

/**
 * The id to report to, or null when config holds nothing usable — an empty
 * value in local development, or a placeholder the deploy pipeline failed to
 * substitute. Null makes the tracker a silent no-op, which is the right failure
 * mode: better no analytics than a tag request against a property that does not
 * exist.
 */
export function resolveMeasurementId(
  configured: string | undefined,
): MeasurementId | null {
  const trimmed = configured?.trim();
  return isMeasurementId(trimmed) ? trimmed : null;
}
